import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import * as unescaper from 'lodash.unescape';
import { createSurveyStyles, StaticContext } from '@sm/webassets';
import { sanitizeString } from '@sm/utils';
import { t, defineMessages } from '@sm/intl';
import { getSurveyQuizFeedback } from './quizScore.graphql';
import './qz-score-summary.scss';
import QuizResultScore from '../QuizResultScore';

const useStyles = createSurveyStyles(theme => {
  return {
    qzScoreSummary: {
      ...theme?.surveyTitle,
      color: theme?.titleColor || theme?.surveyTitle?.color,
      fontFamily: theme?.questionBody?.fontFamily,
    },
  };
});

const DEFAULT_COPY = defineMessages({
  POINTS: {
    id: 'QuizScoreSummary.Points',
    defaultMessage: 'points',
    description: '[Type: Button][Vis.: low] - Points text',
  },
  SCORE: {
    id: 'QuizScoreSummary.Score',
    defaultMessage: 'Score',
    description: '[Type: Text][Vis.: low] - Score text',
  },
});

const getScorePoints = ({ score, maxScore }) => {
  if ((score && maxScore) || score === 0) {
    return (
      <div className="sm-qz-score-summary--points">
        <span>
          {score}/{maxScore} <QuizResultScore type="points" />
        </span>
      </div>
    );
  }
  return null;
};

const getScorePercent = percentScore => {
  if (percentScore === 0 || percentScore) {
    return (
      <div className="sm-qz-score-summary--score">
        {t(DEFAULT_COPY.SCORE)}: {percentScore}%
      </div>
    );
  }
  return null;
};

const getScoreFeedback = (feedback, feedbackImageUrl) => {
  return (
    <>
      {(feedback || feedbackImageUrl) && (
        <div className="sm-qz-score-summary--feedback">
          {feedbackImageUrl && (
            <div className="sm-qz-score-summary--img-container">
              <img src={feedbackImageUrl} alt={feedback || 'Feedback'} className="sm-qz-score-summary--image" />
            </div>
          )}
          {feedback && (
            <div
              className="sm-qz-score-summary--text"
              dangerouslySetInnerHTML={{ __html: sanitizeString(unescaper(feedback)) }}
            />
          )}
        </div>
      )}
    </>
  );
};

const QuizScoreSummary = ({ score, surveyId, base }) => {
  const { qzScoreSummary } = useStyles();
  const qzScore = base === 'percentage' ? `${score.percent}` : `${score.score}`;
  const { sm } = useContext(StaticContext);
  const qzResultsHeaders = {
    'X-SM-PARAM': sm?.smStr || '',
  };
  const {
    loading: loadingFeedback,
    error: errorFeedback,
    data: feedbackData,
  } = useQuery(getSurveyQuizFeedback, {
    context: {
      targetPath: '/lo-graphql/quizResults',
      headers: qzResultsHeaders,
    },
    variables: {
      surveyId,
      score: qzScore,
    },
  });

  if (errorFeedback) {
    throw errorFeedback;
  }

  if (!loadingFeedback && !feedbackData) {
    return null;
  }

  const percentScore = score?.percent === 0 || score?.percent ? Math.round(score?.percent) : '';
  const message = feedbackData?.surveyQuizFeedback?.message;
  const imageUrl = feedbackData?.surveyQuizFeedback?.image?.url;

  return (
    <div className={`sm-qz-score-summary--container ${qzScoreSummary}`} data-testid="QuizScoreSummary__Container">
      {getScoreFeedback(message, imageUrl)}
      {getScorePercent(percentScore)}
      {getScorePoints(score)}
    </div>
  );
};

QuizScoreSummary.propTypes = {
  score: PropTypes.shape({
    feedback: PropTypes.string,
    score: PropTypes.number,
    maxScore: PropTypes.number,
    percent: PropTypes.number,
  }),
  feedback: PropTypes.shape({
    message: PropTypes.string,
    imageUrl: PropTypes.string,
  }),
  surveyId: PropTypes.string.isRequired,
  base: PropTypes.string,
};

QuizScoreSummary.defaultProps = {
  score: {},
  feedback: '',
  base: 'percentage',
};

export default QuizScoreSummary;
